/**
 * №13.1 (§2.11 · §3.1 · §3.2 · §3.4) — Firm settings (`/app/settings/firm`).
 * Player-side, principal-visible setup (not the maker console). Modules
 * contribute panels the declared way. The rate-cards panel makes the resolution
 * precedence legible — the impl ranks **matter > client > firm**, filtered by
 * resource + work-type match; `preview_rate` picks the most specific.
 *
 * §3.1/§3.2 — the option-set editor: **Practice areas** (set `practice_area`,
 * CLOSED) and **Work types** (set `work_type`, OPEN) edit `option-set-option`
 * rows through the synthesised CRUD (M22). Retire = `active:"no"` (never delete,
 * N11); `key`/`open` are module-declared and not editable here (N10). The
 * resolver surfaces every edit to bound fields live.
 *
 * §3.4 — the Time-unit toggle sets `commercial.time_unit`; one `formatUnits`
 * reads it and renders identically on every units surface.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { money } from "../../components/ledger/format";
import { showToast } from "../../components/primitives";
import {
  useEntityType,
  useEntities,
  useCreateEntity,
  useUpdateEntity,
  type EntityRecord,
} from "../../../lib/hooks/use-kuvio";
import { useRateCards, useFeePackages } from "../../hooks/use-time-billing";
import {
  formatUnits,
  useTimeUnitMode,
  useSetTimeUnitMode,
  type TimeUnitMode,
} from "../../lib/format-units";
import { CreateEntityModal } from "./CreateEntityModal";
import { RolesAccessPanel } from "./RolesAccessPanel";

const scopeBadge = (s: unknown) => (s === "matter" ? "badge--tm" : s === "client" ? "badge--fixed" : "badge--soft");

export function FirmSettingsPage() {
  const navigate = useNavigate();
  const rateCards = useRateCards();
  const packages = useFeePackages();
  const rateType = useEntityType("rate-card");
  const [showRate, setShowRate] = useState(false);

  return (
    <div className="screen vt">
      <div className="surface" style={{ marginBottom: 14 }}>
        <div className="surface__bar"><div className="ledger-identity"><span className="statutory">Firm settings</span><span className="bank">Roles &amp; access · Rate cards · Practice areas · Work types · Time unit · Fee packages · Numbering</span></div></div>
      </div>

      {/* №11.2 — Roles & access (who can do what; money closed by default). */}
      <RolesAccessPanel />

      <div className="card card--accent">
        <div className="card__h">Rate resolution <span className="sub">how preview_rate picks a rate</span></div>
        <div className="card__b"><div className="actionbar" style={{ gap: 10 }}>
          <span className="badge badge--tm">1 · Matter</span><span className="muted">→</span>
          <span className="badge badge--fixed">2 · Client</span><span className="muted">→</span>
          <span className="badge badge--soft">3 · Firm</span>
          <span className="sub-note" style={{ margin: "0 0 0 8px" }}>most specific wins · filtered by resource + work-type match</span>
        </div></div>
      </div>

      <div className="card card--flush wide">
        <div className="card__h">Rate cards<span className="spacer" />{rateType.data && <button data-variant="primary" onClick={() => setShowRate(true)}>＋ Rate card</button>}</div>
        {rateCards.isLoading ? <div className="card__b"><div className="skeleton sk-line" style={{ width: "50%" }} /></div>
          : (rateCards.data ?? []).length === 0 ? <div className="card__b muted tiny">No rate cards — firm-wide rates fall back to manual capture.</div>
          : (
            <table className="ledger-table"><thead><tr><th>Scope</th><th>Resource</th><th>Work type</th><th className="num">Rate</th></tr></thead><tbody>
              {rateCards.data!.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/app/place/rate-card/${r.id}`)} style={{ cursor: "pointer" }}>
                  <td><span className={`badge ${scopeBadge(r.scope)}`}>{(r.scope as string) ?? "firm"}</span></td>
                  <td>{(r.resource as string) ?? "—"}</td>
                  <td>{(r.work_type as string) ?? "Any"}</td>
                  <td className="num money">{money((r.rate_minor as number) ?? 0)}</td>
                </tr>
              ))}
            </tbody></table>
          )}
      </div>

      {/* §3.1/§3.2 — option-set editors. Practice areas = CLOSED; Work types = OPEN. */}
      <div className="grid2">
        <OptionSetPanel setKey="practice_area" title="Practice areas" />
        <OptionSetPanel setKey="work_type" title="Work types" />
      </div>

      {/* §3.4 — one time-capture unit → one formatter, system-wide. */}
      <TimeUnitPanel />

      <div className="card card--flush">
        <div className="card__h">Fee packages<span className="spacer" /></div>
        <table className="ledger-table"><thead><tr><th>Package</th><th>Method</th></tr></thead><tbody>
          {(packages.data ?? []).length === 0 ? <tr><td className="muted tiny" colSpan={2}>No fee packages defined</td></tr>
            : packages.data!.map((p) => (
              <tr key={p.id} onClick={() => navigate(`/app/place/fee-package/${p.id}`)} style={{ cursor: "pointer" }}>
                <td>{(p.code as string) ?? p.id}</td>
                <td><span className={`badge ${p.billing_method === "fixed" ? "badge--fixed" : "badge--tm"}`}>{p.billing_method === "fixed" ? "Fixed" : "T&M"}</span></td>
              </tr>
            ))}
        </tbody></table>
      </div>

      <div className="card"><div className="card__h">Numbering <span className="sub">read-only — rides id_prefix</span></div>
        <div className="card__b tiny sec">Matter number = matter id (MAT-####) · Invoice number = invoice id (INV-####) · Register = REG-#### · the old <code>allocate_sequence</code> effect is retired (№11.1.4).</div>
      </div>

      {rateType.data && <CreateEntityModal entityType={rateType.data} open={showRate} onClose={() => setShowRate(false)} onSaved={() => { setShowRate(false); void rateCards.refetch(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// §3.4 — Time-unit toggle. Writes `commercial.time_unit`; the live example makes
// the effect legible. `units` stays a raw number — the formatter is display-only.
// ---------------------------------------------------------------------------

function TimeUnitPanel() {
  const { mode, isLoading } = useTimeUnitMode();
  const setMode = useSetTimeUnitMode();

  async function choose(next: TimeUnitMode) {
    if (next === mode || setMode.isPending) return;
    try {
      await setMode.mutateAsync(next);
      showToast(`Time unit set to ${next === "decimal_hours" ? "decimal hours" : "six-minute units"}`, "success");
    } catch (e) {
      showToast((e as Error).message, "error");
    }
  }

  return (
    <div className="card card--accent">
      <div className="card__h">Unit display <span className="sub">update_workspace_settings {"{ time_unit }"} — one formatter, every surface</span></div>
      <div className="card__b">
        <div className="actionbar" style={{ gap: 12, flexWrap: "wrap" }}>
          <div className="type-switch" role="group" aria-label="time unit">
            <button aria-selected={mode === "six_minute"} disabled={isLoading || setMode.isPending} onClick={() => void choose("six_minute")}>Six-minute units</button>
            <button aria-selected={mode === "decimal_hours"} disabled={isLoading || setMode.isPending} onClick={() => void choose("decimal_hours")}>Decimal hours</button>
          </div>
          <span className="sub-note" style={{ margin: 0 }}>
            e.g. 25 raw units → <b>{formatUnits(25, mode)}</b>
          </span>
        </div>
        <p className="sub-note" style={{ marginTop: 8 }}>
          <code>formatUnits(rawUnits, mode)</code> reads this setting and renders identically on day view, matter capture, the WIP worksheet and invoice lines. <code>six_minute</code> → <b>N units (= H.h hrs)</b>; <code>decimal_hours</code> → <b>H.h hrs</b>. The stored <code>units</code> value never changes.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// §3.1/§3.2 — option-set panel. Lists a set's `option-set-option` rows ordered by
// sort_order; add / rename (label) / reorder / retire (active:"no"). Editing is
// options-only — `key` and `open` are module-declared and shown, never edited.
// ---------------------------------------------------------------------------

function OptionSetPanel({ setKey, title }: { setKey: string; title: string }) {
  const setType = useEntityType("option-set");
  const optType = useEntityType("option-set-option");
  const sets = useEntities(setType.data);
  const opts = useEntities(optType.data);
  const create = useCreateEntity(optType.data);
  const update = useUpdateEntity(optType.data);

  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");

  const loading = setType.isLoading || optType.isLoading || sets.isLoading || opts.isLoading;
  const set = (sets.data?.entities ?? []).find((s) => s.key === setKey);
  const isOpen = String(set?.open) === "yes";
  const options = (opts.data?.entities ?? [])
    .filter((o) => o.option_set === set?.id)
    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));

  async function add() {
    const v = value.trim();
    if (!set || !v) return;
    const maxSort = options.reduce((m, o) => Math.max(m, Number(o.sort_order) || 0), -1);
    try {
      await create.mutateAsync({ option_set: set.id, value: v, label: label.trim() || v, active: "yes", sort_order: maxSort + 1 });
      setValue(""); setLabel("");
    } catch (e) {
      showToast((e as Error).message, "error");
    }
  }

  async function rename(o: EntityRecord, next: string) {
    const trimmed = next.trim();
    if (!trimmed || trimmed === o.label) return;
    try { await update.mutateAsync({ id: o.id, fields: { label: trimmed } }); }
    catch (e) { showToast((e as Error).message, "error"); }
  }

  async function setActive(o: EntityRecord, active: "yes" | "no") {
    try { await update.mutateAsync({ id: o.id, fields: { active } }); }
    catch (e) { showToast((e as Error).message, "error"); }
  }

  async function move(idx: number, dir: -1 | 1) {
    const a = options[idx];
    const b = options[idx + dir];
    if (!a || !b) return;
    const sa = Number(a.sort_order) || 0;
    const sb = Number(b.sort_order) || 0;
    // Distinct target orders even if the stored values collide (idx-anchored).
    const [na, nb] = sa === sb ? [idx + dir, idx] : [sb, sa];
    try {
      await Promise.all([
        update.mutateAsync({ id: a.id, fields: { sort_order: na } }),
        update.mutateAsync({ id: b.id, fields: { sort_order: nb } }),
      ]);
    } catch (e) { showToast((e as Error).message, "error"); }
  }

  return (
    <div className="card card--flush">
      <div className="card__h">
        {title}
        <span className="spacer" />
        <span className={`badge ${isOpen ? "badge--tm" : "badge--soft"}`}>{isOpen ? "Open" : "Closed"}</span>
      </div>
      <div className="card__b tiny sec" style={{ paddingBottom: 6 }}>
        {isOpen ? "suggested — capture accepts any value" : "records must use one of these"}
      </div>

      {loading ? (
        <div className="card__b"><div className="skeleton sk-line" style={{ width: "60%" }} /></div>
      ) : !set ? (
        <div className="card__b muted tiny">This option set is not installed in this workspace.</div>
      ) : (
        <>
          <table className="ledger-table"><tbody>
            {options.length === 0 ? <tr><td className="muted tiny" colSpan={3}>No options yet — add one below.</td></tr> : options.map((o, idx) => {
              const retired = String(o.active) === "no";
              return (
                <tr key={o.id} style={retired ? { opacity: 0.5 } : undefined}>
                  <td style={{ width: 18, color: "var(--vt-text-3, #999)", cursor: "grab", userSelect: "none" }} title="sort order" aria-hidden>⠿</td>
                  <td>
                    <EditableLabel value={(o.label as string) ?? (o.value as string)} onCommit={(next) => void rename(o, next)} />
                    <span className="muted tiny" style={{ marginLeft: 8 }}><code>{o.value as string}</code></span>
                    {retired ? <span className="badge badge--soft" style={{ marginLeft: 8 }}>retired</span> : null}
                  </td>
                  <td className="num" style={{ whiteSpace: "nowrap" }}>
                    <button className="drill" style={{ border: "none", background: "none", cursor: "pointer" }} disabled={idx === 0 || update.isPending} onClick={() => void move(idx, -1)} aria-label="move up" title="Move up">↑</button>
                    <button className="drill" style={{ border: "none", background: "none", cursor: "pointer" }} disabled={idx === options.length - 1 || update.isPending} onClick={() => void move(idx, 1)} aria-label="move down" title="Move down">↓</button>
                    {retired
                      ? <button className="drill" style={{ border: "none", background: "none", cursor: "pointer", marginLeft: 6 }} disabled={update.isPending} onClick={() => void setActive(o, "yes")}>Restore</button>
                      : <button className="drill" style={{ border: "none", background: "none", cursor: "pointer", marginLeft: 6 }} disabled={update.isPending} onClick={() => void setActive(o, "no")}>Retire</button>}
                  </td>
                </tr>
              );
            })}
          </tbody></table>

          <div className="card__b">
            <div className="actionbar" style={{ gap: 8, flexWrap: "wrap" }}>
              <input className="cem-input" style={{ padding: "6px 8px" }} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (shown in pickers)" aria-label="new option label" />
              <input className="cem-input" style={{ padding: "6px 8px", width: 140 }} value={value} onChange={(e) => setValue(e.target.value)} placeholder="value (stored code)" aria-label="new option value" />
              <button data-variant="primary" disabled={!value.trim() || create.isPending} onClick={() => void add()}>{create.isPending ? "…" : "＋ Add option"}</button>
            </div>
            <p className="sub-note" style={{ margin: "6px 0 0" }}>Rename edits the label only; the stored <code>value</code> is stable. Retire keeps the value on existing records but drops it from new pickers. The <code>key</code> and open/closed mode are module-declared and can't be changed here.</p>
          </div>
        </>
      )}
    </div>
  );
}

/** Click-to-edit label cell — Enter/blur commits, Esc cancels. */
function EditableLabel({ value, onCommit }: { value: string; onCommit: (next: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <span
        role="button"
        tabIndex={0}
        style={{ cursor: "text", borderBottom: "1px dashed transparent" }}
        onClick={() => { setDraft(value); setEditing(true); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDraft(value); setEditing(true); } }}
      >
        {value}
      </span>
    );
  }
  return (
    <input
      className="cem-input"
      style={{ padding: "2px 6px" }}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setEditing(false); onCommit(draft); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.currentTarget.blur(); }
        else if (e.key === "Escape") { setDraft(value); setEditing(false); }
      }}
      aria-label="edit label"
    />
  );
}
